/**
 * Create Event page -- form for creating a new mix event
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2, Loader2, Save, X, Info } from "lucide-react";
import { createEvent, getEvent } from "@/lib/api";
import type { CreateEventInput, EventType, OutputTarget } from "@/lib/store";
import { EventTypeSelector } from "@/components/EventTypeSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// ---------------------------------------------------------------------------
// Event Templates (localStorage)
// ---------------------------------------------------------------------------

interface EventTemplate {
  name: string;
  eventType: EventType;
  geoRadiusMeters: number;
  geoRequired: boolean;
  maxParticipants: number;
  maxRecordingSeconds: number;
  maxFileSizeMb: number;
  outputTargets: { style: OutputTarget["style"]; count: number }[];
  recordingPrompt: string;
}

const TEMPLATES_KEY = "vizimix-event-templates";

function loadTemplates(): EventTemplate[] {
  try {
    const raw = localStorage.getItem(TEMPLATES_KEY);
    return raw ? (JSON.parse(raw) as EventTemplate[]) : [];
  } catch {
    return [];
  }
}

function saveTemplates(templates: EventTemplate[]) {
  localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
}

function toUnixSeconds(datetimeLocal: string): number {
  return Math.floor(new Date(datetimeLocal).getTime() / 1000);
}

function defaultStartTime(): string {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return d.toISOString().slice(0, 16);
}

function defaultEndTime(): string {
  const d = new Date();
  d.setHours(d.getHours() + 3, 0, 0, 0);
  return d.toISOString().slice(0, 16);
}

function toDatetimeLocal(unix: number): string {
  const d = new Date(unix * 1000);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function shiftDays(datetimeLocal: string, days: number): string {
  const d = new Date(datetimeLocal);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 16);
}

export function CreateEvent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromId = searchParams.get("from");

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventType, setEventType] = useState<EventType>("general");
  const [startsAt, setStartsAt] = useState(defaultStartTime());
  const [endsAt, setEndsAt] = useState(defaultEndTime());
  const [geoLatitude, setGeoLatitude] = useState("");
  const [geoLongitude, setGeoLongitude] = useState("");
  const [geoRadiusMeters, setGeoRadiusMeters] = useState(1000);
  const [geoRequired, setGeoRequired] = useState(false);
  const [maxParticipants, setMaxParticipants] = useState(200);
  const [maxRecordingSeconds, setMaxRecordingSeconds] = useState(120);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(500);
  const [outputTargets, setOutputTargets] = useState<
    { style: OutputTarget["style"]; count: number }[]
  >([{ style: "tiktok", count: 1 }]);
  const [recordingPrompt, setRecordingPrompt] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [templates, setTemplates] = useState<EventTemplate[]>(loadTemplates);
  const [templateName, setTemplateName] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  // Load source event for duplication
  const { data: sourceEvent } = useQuery({
    queryKey: ["event", fromId],
    queryFn: () => getEvent(fromId!),
    enabled: !!fromId && !prefilled,
  });

  // Pre-fill form when source event loads
  useEffect(() => {
    if (!sourceEvent || prefilled) return;
    setTitle(`Copy of ${sourceEvent.title}`);
    setDescription(sourceEvent.description || "");
    setEventType(sourceEvent.eventType);
    // Shift dates +7 days from original
    const origStart = toDatetimeLocal(sourceEvent.startsAt);
    const origEnd = toDatetimeLocal(sourceEvent.endsAt);
    setStartsAt(shiftDays(origStart, 7));
    setEndsAt(shiftDays(origEnd, 7));
    if (sourceEvent.geoLatitude) setGeoLatitude(sourceEvent.geoLatitude.toString());
    if (sourceEvent.geoLongitude) setGeoLongitude(sourceEvent.geoLongitude.toString());
    setGeoRadiusMeters(sourceEvent.geoRadiusMeters);
    setGeoRequired(sourceEvent.geoRequired);
    setMaxParticipants(sourceEvent.maxParticipants);
    setMaxRecordingSeconds(sourceEvent.maxRecordingSeconds);
    setMaxFileSizeMb(sourceEvent.maxFileSizeMb);
    if (sourceEvent.outputTargets) {
      setOutputTargets(sourceEvent.outputTargets.map((t) => ({ style: t.style, count: t.count })));
    }
    setRecordingPrompt(sourceEvent.recordingPrompt || "");
    setPrefilled(true);
  }, [sourceEvent, prefilled]);

  const mutation = useMutation({
    mutationFn: (data: CreateEventInput) => createEvent(data),
    onSuccess: (event) => {
      navigate(`/events/${event.id}`);
    },
  });

  function addOutputTarget() {
    setOutputTargets([...outputTargets, { style: "tiktok", count: 1 }]);
  }

  function removeOutputTarget(index: number) {
    setOutputTargets(outputTargets.filter((_, i) => i !== index));
  }

  function updateOutputTarget(
    index: number,
    field: "style" | "count",
    value: string | number
  ) {
    const updated = [...outputTargets];
    if (field === "style") {
      updated[index] = { ...updated[index], style: value as OutputTarget["style"] };
    } else {
      updated[index] = { ...updated[index], count: value as number };
    }
    setOutputTargets(updated);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;

    const data: CreateEventInput = {
      title: title.trim(),
      description: description.trim() || undefined,
      eventType,
      startsAt: toUnixSeconds(startsAt),
      endsAt: toUnixSeconds(endsAt),
      geoRequired,
      geoRadiusMeters,
      maxParticipants,
      maxRecordingSeconds,
      maxFileSizeMb,
      outputTargets: outputTargets.length > 0 ? outputTargets : undefined,
      recordingPrompt: recordingPrompt.trim() || undefined,
    };

    if (geoLatitude) data.geoLatitude = parseFloat(geoLatitude);
    if (geoLongitude) data.geoLongitude = parseFloat(geoLongitude);

    mutation.mutate(data);
  }

  function handleSaveTemplate() {
    const name = templateName.trim();
    if (!name) return;
    const template: EventTemplate = {
      name,
      eventType,
      geoRadiusMeters,
      geoRequired,
      maxParticipants,
      maxRecordingSeconds,
      maxFileSizeMb,
      outputTargets,
      recordingPrompt,
    };
    const updated = [...templates.filter((t) => t.name !== name), template];
    setTemplates(updated);
    saveTemplates(updated);
    setTemplateName("");
    setShowSaveTemplate(false);
  }

  function handleLoadTemplate(template: EventTemplate) {
    setEventType(template.eventType);
    setGeoRadiusMeters(template.geoRadiusMeters);
    setGeoRequired(template.geoRequired);
    setMaxParticipants(template.maxParticipants);
    setMaxRecordingSeconds(template.maxRecordingSeconds);
    setMaxFileSizeMb(template.maxFileSizeMb);
    setOutputTargets(template.outputTargets);
    setRecordingPrompt(template.recordingPrompt);
  }

  function handleDeleteTemplate(name: string) {
    const updated = templates.filter((t) => t.name !== name);
    setTemplates(updated);
    saveTemplates(updated);
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-vizimix-100">Create Event</h2>
          <p className="text-sm text-vizimix-400 mt-1">
            Set up a new event for collecting participant videos
          </p>
        </div>
      </div>

      {/* Templates */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {templates.map((t) => (
          <div
            key={t.name}
            className="flex items-center gap-1 rounded-md border border-organize-border bg-organize-card px-3 py-1.5 text-sm"
          >
            <button
              onClick={() => handleLoadTemplate(t)}
              className="text-vizimix-200 hover:text-vizimix-100 transition-colors"
            >
              {t.name}
            </button>
            <button
              onClick={() => handleDeleteTemplate(t.name)}
              className="text-vizimix-500 hover:text-red-400 transition-colors ml-1"
              aria-label={`Delete template ${t.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {showSaveTemplate ? (
          <div className="flex items-center gap-2">
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template name"
              className="h-8 w-40 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); handleSaveTemplate(); }
                if (e.key === "Escape") setShowSaveTemplate(false);
              }}
            />
            <Button size="sm" variant="outline" onClick={handleSaveTemplate} disabled={!templateName.trim()}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowSaveTemplate(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowSaveTemplate(true)}
            className="gap-1"
          >
            <Save className="h-3 w-3" />
            Save as Template
          </Button>
        )}
      </div>

      {mutation.error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6 text-sm text-destructive">
          {mutation.error instanceof Error
            ? mutation.error.message
            : "Failed to create event"}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-vizimix-200 mb-2">
                Title *
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My event title"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-vizimix-200 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your event..."
                rows={3}
                className="flex w-full rounded-md border border-organize-border bg-organize-bg px-3 py-2 text-sm text-vizimix-100 placeholder:text-vizimix-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vizimix-500 focus-visible:ring-offset-2 focus-visible:ring-offset-organize-bg"
              />
            </div>
            <EventTypeSelector value={eventType} onChange={setEventType} />
          </CardContent>
        </Card>

        {/* Date/Time */}
        <Card>
          <CardHeader>
            <CardTitle>Date & Time</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-vizimix-200 mb-2">
                  Start Date/Time *
                </label>
                <Input
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-vizimix-200 mb-2">
                  End Date/Time *
                </label>
                <Input
                  type="datetime-local"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  required
                />
              </div>
            </div>
            {startsAt && (
              <div className="flex items-start gap-2 rounded-md border border-blue-500/30 bg-blue-500/10 px-3 py-2.5 text-sm text-blue-300">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  This event will auto-start at{" "}
                  <strong>
                    {new Date(startsAt).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </strong>
                  . Draft events are automatically activated when their start time
                  arrives.
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location */}
        <Card>
          <CardHeader>
            <CardTitle>Location (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-vizimix-200 mb-2">
                  Latitude
                </label>
                <Input
                  type="number"
                  step="any"
                  value={geoLatitude}
                  onChange={(e) => setGeoLatitude(e.target.value)}
                  placeholder="40.7128"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-vizimix-200 mb-2">
                  Longitude
                </label>
                <Input
                  type="number"
                  step="any"
                  value={geoLongitude}
                  onChange={(e) => setGeoLongitude(e.target.value)}
                  placeholder="-74.0060"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-vizimix-200 mb-2">
                Radius: {geoRadiusMeters}m
              </label>
              <input
                type="range"
                min={100}
                max={5000}
                step={100}
                value={geoRadiusMeters}
                onChange={(e) => setGeoRadiusMeters(Number(e.target.value))}
                className="w-full accent-vizimix-600"
              />
              <div className="flex justify-between text-xs text-vizimix-500 mt-1">
                <span>100m</span>
                <span>5000m</span>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-vizimix-200 cursor-pointer">
              <input
                type="checkbox"
                checked={geoRequired}
                onChange={(e) => setGeoRequired(e.target.checked)}
                className="rounded border-organize-border accent-vizimix-600"
              />
              Require location verification for participants
            </label>
          </CardContent>
        </Card>

        {/* Limits */}
        <Card>
          <CardHeader>
            <CardTitle>Limits</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-vizimix-200 mb-2">
                Max Participants
              </label>
              <Input
                type="number"
                min={1}
                max={10000}
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-vizimix-200 mb-2">
                Max Recording Duration: {maxRecordingSeconds}s (
                {Math.floor(maxRecordingSeconds / 60)}:
                {(maxRecordingSeconds % 60).toString().padStart(2, "0")})
              </label>
              <input
                type="range"
                min={30}
                max={300}
                step={10}
                value={maxRecordingSeconds}
                onChange={(e) =>
                  setMaxRecordingSeconds(Number(e.target.value))
                }
                className="w-full accent-vizimix-600"
              />
              <div className="flex justify-between text-xs text-vizimix-500 mt-1">
                <span>30s</span>
                <span>300s</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-vizimix-200 mb-2">
                Max File Size (MB)
              </label>
              <Input
                type="number"
                min={10}
                max={2000}
                value={maxFileSizeMb}
                onChange={(e) => setMaxFileSizeMb(Number(e.target.value))}
              />
            </div>
          </CardContent>
        </Card>

        {/* Recording Prompt */}
        <Card>
          <CardHeader>
            <CardTitle>Recording Prompt (Optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={recordingPrompt}
              onChange={(e) => setRecordingPrompt(e.target.value)}
              placeholder="e.g. &quot;Show us your best dance move!&quot; or &quot;Tell us what this event means to you&quot;"
              rows={2}
              maxLength={200}
              className="flex w-full rounded-md border border-organize-border bg-organize-bg px-3 py-2 text-sm text-vizimix-100 placeholder:text-vizimix-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vizimix-500 focus-visible:ring-offset-2 focus-visible:ring-offset-organize-bg"
            />
            <p className="text-xs text-vizimix-500 mt-1">
              This prompt will be shown to participants during recording.
            </p>
          </CardContent>
        </Card>

        {/* Output Targets */}
        <Card>
          <CardHeader>
            <CardTitle>Output Targets</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {outputTargets.map((target, index) => (
              <div key={index} className="flex items-center gap-3">
                <select
                  value={target.style}
                  onChange={(e) =>
                    updateOutputTarget(index, "style", e.target.value)
                  }
                  className="flex h-10 rounded-md border border-organize-border bg-organize-bg px-3 py-2 text-sm text-vizimix-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vizimix-500"
                >
                  <option value="tiktok">TikTok</option>
                  <option value="instagram">Instagram</option>
                  <option value="youtube_short">YouTube Short</option>
                </select>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={target.count}
                  onChange={(e) =>
                    updateOutputTarget(index, "count", Number(e.target.value))
                  }
                  className="w-24"
                  placeholder="Count"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeOutputTarget(index)}
                  disabled={outputTargets.length <= 1}
                >
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addOutputTarget}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Output
            </Button>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" type="button" onClick={() => navigate("/")}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!title.trim() || mutation.isPending}
          >
            {mutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            )}
            Create Event
          </Button>
        </div>
      </form>
    </div>
  );
}
